import { useState, useEffect, useCallback } from 'react';
import { 
  getCountries, 
  getStatesByCountry, 
  getCitiesByState, 
  Country, 
  State, 
  City 
} from '../services/locationAPI';

interface UseLocationDataReturn {
  countries: Country[];
  states: State[];
  cities: City[];
  loading: {
    countries: boolean;
    states: boolean;
    cities: boolean;
  };
  error: {
    countries: string | null;
    states: string | null;
    cities: string | null;
  };
  loadCountries: () => Promise<void>;
  loadStates: (countryIso2: string) => Promise<void>;
  loadCities: (countryIso2: string, stateIso2: string) => Promise<void>;
  clearStates: () => void;
  clearCities: () => void;
  reset: () => void;
}

// Simple cache to avoid repeated API calls
const cache = {
  countries: null as Country[] | null,
  states: new Map<string, State[]>(),
  cities: new Map<string, City[]>(),
};

export const useLocationData = (): UseLocationDataReturn => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  
  const [loading, setLoading] = useState({
    countries: false,
    states: false,
    cities: false,
  });
  
  const [error, setError] = useState({
    countries: null as string | null,
    states: null as string | null,
    cities: null as string | null,
  });

  // Load countries
  const loadCountries = useCallback(async () => {
    if (cache.countries) {
      setCountries(cache.countries);
      return;
    }

    setLoading(prev => ({ ...prev, countries: true }));
    setError(prev => ({ ...prev, countries: null }));

    try {
      const countriesData = await getCountries();
      cache.countries = countriesData;
      setCountries(countriesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load countries';
      setError(prev => ({ ...prev, countries: errorMessage }));
      console.error('Error loading countries:', err);
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  }, []);

  // Load states for a country
  const loadStates = useCallback(async (countryIso2: string) => {
    if (!countryIso2) {
      setStates([]);
      return;
    }

    // Check cache first
    if (cache.states.has(countryIso2)) {
      setStates(cache.states.get(countryIso2)!);
      return;
    }

    setLoading(prev => ({ ...prev, states: true }));
    setError(prev => ({ ...prev, states: null }));

    try {
      const statesData = await getStatesByCountry(countryIso2);
      cache.states.set(countryIso2, statesData);
      setStates(statesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load states';
      setError(prev => ({ ...prev, states: errorMessage }));
      console.error('Error loading states:', err);
      setStates([]);
    } finally {
      setLoading(prev => ({ ...prev, states: false }));
    }
  }, []);

  // Load cities for a state
  const loadCities = useCallback(async (countryIso2: string, stateIso2: string) => {
    if (!countryIso2 || !stateIso2) {
      setCities([]);
      return;
    }

    const cacheKey = `${countryIso2}-${stateIso2}`;
    
    // Check cache first
    if (cache.cities.has(cacheKey)) {
      setCities(cache.cities.get(cacheKey)!);
      return;
    }

    setLoading(prev => ({ ...prev, cities: true }));
    setError(prev => ({ ...prev, cities: null }));

    try {
      const citiesData = await getCitiesByState(countryIso2, stateIso2);
      cache.cities.set(cacheKey, citiesData);
      setCities(citiesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load cities';
      setError(prev => ({ ...prev, cities: errorMessage }));
      console.error('Error loading cities:', err);
      setCities([]);
    } finally {
      setLoading(prev => ({ ...prev, cities: false }));
    }
  }, []);

  // Clear states and cities
  const clearStates = useCallback(() => {
    setStates([]);
    setCities([]);
  }, []);

  // Clear cities only
  const clearCities = useCallback(() => {
    setCities([]);
  }, []);

  // Reset all data
  const reset = useCallback(() => {
    setCountries([]);
    setStates([]);
    setCities([]);
    setLoading({
      countries: false,
      states: false,
      cities: false,
    });
    setError({
      countries: null,
      states: null,
      cities: null,
    });
  }, []);

  // Auto-load countries on mount
  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  return {
    countries,
    states,
    cities,
    loading,
    error,
    loadCountries,
    loadStates,
    loadCities,
    clearStates,
    clearCities,
    reset,
  };
}; 